<template>
  <div v-if="validationError" class="fs-overlay-error">
  <div class="error-message">
  <h3>{{$t('messages.config_error')}}</h3>
  <p>{{ validationError }}</p>
  </div>
  </div>
  <div v-else class="fs-overlay">
    <div class="fs-form fs-select" v-if="selected">
      <span class="fs-label icon-opacity">
        <span class="icon">
          <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12.7866" cy="13.4338" r="7.18487" stroke="#516066" stroke-width="2.05714" />
            <circle cx="8.52765" cy="8.56641" r="7.18487" stroke="#516066" stroke-width="2.05714" />
            <path fill-rule="evenodd" clip-rule="evenodd" d="M16.2486 6.03027C15.2099 5.55148 14.0534 5.28442 12.8345 5.28442C8.32488 5.28442 4.6691 8.9402 4.6691 13.4498C4.6691 14.3294 4.80816 15.1764 5.0655 15.9702C2.26089 14.6775 0.314209 11.8414 0.314209 8.55065C0.314209 4.04102 3.96999 0.385239 8.47962 0.385239C12.1097 0.385239 15.1865 2.75407 16.2486 6.03027Z" fill="#516066" />
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.43094 5.79856C9.77715 5.66913 10.1345 5.56252 10.5011 5.48057L15.777 10.834C15.6748 11.1858 15.5495 11.5278 15.4029 11.8583L9.43094 5.79856ZM7.23035 7.04014C7.4915 6.8337 7.76557 6.64287 8.05115 6.46908L14.6634 13.1785C14.475 13.4518 14.2704 13.7131 14.0509 13.961L7.23035 7.04014ZM5.56179 8.82157C5.75017 8.54823 5.95477 8.28693 6.1742 8.03907L12.9948 14.9599C12.7336 15.1663 12.4595 15.3571 12.174 15.5309L5.56179 8.82157ZM4.44817 11.1661C4.55043 10.8143 4.67572 10.4723 4.82231 10.1418L10.7941 16.2014C10.4479 16.3308 10.0906 16.4374 9.72391 16.5194L4.44817 11.1661ZM4.17524 14.3637C4.1418 14.0635 4.12463 13.7585 4.12463 13.4494C4.12463 13.2733 4.13021 13.0985 4.14119 12.9252L7.87677 16.7157C7.35118 16.712 6.8374 16.6587 6.33988 16.5601L4.17524 14.3637ZM16.0841 9.07495L12.3482 5.28421C12.8738 5.28788 13.3876 5.3412 13.8851 5.43971L16.0501 7.63653C16.0835 7.93657 16.1006 8.24152 16.1006 8.55048C16.1006 8.72666 16.0951 8.90154 16.0841 9.07495Z" fill="#516066" />
        </svg>
        </span>
      </span>
      <select v-model="settingsModel.opacity" @change="onChange">
        <option v-for="item in opacityOptions" :key="item" :value="item">
          {{ `${item}%` }}
        </option>
      </select>
    </div>
    <div class="fs-menu-separator" v-if="selected"></div>
    <div>
      <fs-button
        @click="openPicker()"
        type="primary"
        class="fs-btn-s"
        size="small"
        data-e2e="open-picker-done"
      >{{ $t('menu.add_overlay_image') }}</fs-button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Inject, Component } from 'vue-property-decorator';
import { Getter, Mutation } from 'vuex-class';
import debounce from 'lodash.debounce';

import { FsCanvas, CANVAS_EVENTS, OverlaySettings, OverlayLayer, AbstractLayer } from '@filestack/canvas-sdk';
import { Client, PickerOptions } from 'filestack-js';
import { checkImageUrl } from '@/helpers/image';
import { authorizeUrl } from '@/helpers/security';
import { ToastMessage, ToastMessageType } from '@/types';
import { numbersSequence } from '@/helpers/array';

const DEBOUNCE_TIME = 250;

@Component
class OverlayBottom extends Vue {
  protected layerDebounceHandler: any;

  private _settingsModel: OverlaySettings = {
    opacity: 100,
  };
  
  private validationError: string = null;
  private customOpacityValues: number[] = [];
 
  private validateOverlayOpacity(opacity: number) {
    if (!Number.isInteger(opacity) || opacity < 1 || opacity > 100) {
      throw new Error(
        this.$t(`messages.invalid_opacity_error`, { opacity }) as string
      );
    }
  }

  get settingsModel(): OverlaySettings {
    if (this.selected instanceof OverlayLayer) {
      const settings = this.selected.getSettings();
      return {
        ...settings,
        opacity: Math.round(settings.opacity)
      };

    }
    return this.defaultOverlaySettings;
  }


  @Inject('canvas')
  private canvas: FsCanvas;

  @Inject('sdk')
  private sdk: Client;

  @Getter('config')
  private config: any;

  private selected: OverlayLayer = null;

  get overlayConfig() {
    // @ts-ignore @todo
    return this.config('editor.overlayConfig');
  }

  protected get numbersSequence() {
    return numbersSequence;
  }

  protected get opacityOptions() {
    const standardOptions = numbersSequence(100, 5);
    const currentOpacity = Math.round(this.settingsModel.opacity);
    

    if (
    currentOpacity &&
    !standardOptions.includes(currentOpacity) &&
    !this.customOpacityValues.includes(currentOpacity)
  ) {
    this.customOpacityValues.push(currentOpacity);
  }

  const allOptions = [...standardOptions, ...this.customOpacityValues];
  const sortedOptions = allOptions.sort((a, b) => a - b);

  return sortedOptions;
}

  @Mutation('toast/show')
  private showToast: (data: ToastMessage) => void;


  private evHandler: any;

  private selectionChangedCallback(layer: AbstractLayer) {
    if (layer && layer instanceof OverlayLayer) {
      this.selected = layer;
    }
  }

  private get defaultOverlaySettings(): OverlaySettings {
    return {
      ...this.config('editor.overlay.defaults'),
    };
  }

  private mounted() {
    // @ts-ignore
    this.selected = this.$route.params.selected;

    const opacityValue = this.config('editor.overlay.defaults.opacity');
    try {
      this.validateOverlayOpacity(opacityValue);
    } catch (error) {
      console.error('Overlay opacity validation failed:', (error as Error).message);
      this.validationError = (error as Error).message;
      return;
    }
    
    this.canvas.on(CANVAS_EVENTS.LAYER_MODIFIED, this.onLayerModified);
    this.canvas.on(CANVAS_EVENTS.SELECTION_CHANGED, this.selectionChangedCallback);

    // setup initial dimensions
    document.addEventListener('keydown', this.evHandler);
    }

  private destroyed() {
    this.canvas.off(CANVAS_EVENTS.LAYER_MODIFIED, this.onLayerModified);
    this.canvas.off(CANVAS_EVENTS.SELECTION_CHANGED, this.selectionChangedCallback);
    document.removeEventListener('keydown', this.evHandler);
  }

  private openPicker() {
    const error = () => this.showToast({
      message: 'messages.overlays_error_loading',
      type: ToastMessageType.DANGER,
      icon: 'error',
    });

    const options: PickerOptions = {
      rootId: '__filestack-picker-overlay',
      uploadInBackground: false,
      maxFiles: 1,
      accept: ['image/*'],
      imageMax: [2000, 2000],
      maxSize: 15 * 1024 * 1024,
      lang: this.config('lang') || 'en',
      disableTransformer: true,
      onUploadDone: (res: any) => {
        if (res.filesUploaded && res.filesUploaded.length === 1) {
          let imageUrl = res.filesUploaded[0].url;
          if (this.sdk.session.signature) {
            imageUrl = authorizeUrl(
              imageUrl,
              this.sdk.session.policy,
              this.sdk.session.signature);
          }
          return checkImageUrl(imageUrl).then(() => {
            // this.canvas.overlay.addLayer(imageUrl).then(() => this.canvas.saveState('overlayLayerAdd'));
            OverlayLayer.createFromURL(imageUrl, {
              crossOrigin: 'anonymous',
            }).then((img: OverlayLayer) => {
                this.canvas.layer.add(img);
                // Set default opacity from store configuration
                img.setOpacity(this.config('editor.overlay.defaults.opacity'));
                this.canvas.render();
                this.canvas.saveState('overlayLayerAdd');
            });
          }).catch(error);
        }

        error();
      },
    };

    if (this.overlayConfig && this.overlayConfig.length) {
      options.fromSources = this.overlayConfig.fromSources;
    }

    this.sdk.picker(options).open();
  }

  private onChange() {
    // @ts-ignore
    this.selected.setOpacity(this.settingsModel.opacity);
    this.canvas.saveState('overlaySettingsUpdate');
  }

  private onLayerModified() {
    if (!this.layerDebounceHandler) {
      this.layerDebounceHandler = debounce(() => {
          this.canvas.saveState('overlayLayerModified');
      }, DEBOUNCE_TIME);
    }

    this.layerDebounceHandler();
  }
}

export default OverlayBottom;
</script>
